import { Injectable, inject } from '@angular/core';
import { Observable, from, map, shareReplay, switchMap } from 'rxjs';
import {
  DomainSignalId,
  Entity,
  EntityApplications,
  EntityPermissionActivityKeys,
  UserRoleListItemResponse,
} from '@ag-common-lib/public-api';
import { DomainSignalsService } from './domain-signals.service';
import { IdentityApiService } from './identity-api.service';

/**
 * One shared, cached list of roles for display purposes — resolving a role id to a
 * name, and filling role pickers.
 *
 * It exists to kill a specific pathology: the two role-name pipes were pure pipes
 * that each opened their own full-collection Firestore listener, and they are used
 * inside *ngFor cell templates, so a grid opened one listener per rendered row.
 * This is a single HTTP call shared by every consumer, refreshed on the same signal
 * as permissions.
 */
@Injectable({ providedIn: 'root' })
export class UserRolesCatalogService {
  private readonly identityApi = inject(IdentityApiService);
  private readonly domainSignals = inject(DomainSignalsService);

  private readonly catalogByApp = new Map<string, Observable<UserRoleListItemResponse[]>>();

  getRoles(appId?: EntityApplications, includeDisabled = true): Observable<UserRoleListItemResponse[]> {
    const key = `${appId ?? 'all'}:${includeDisabled}`;
    const cached = this.catalogByApp.get(key);
    if (cached) {
      return cached;
    }

    const catalog$ = this.domainSignals.getSignalVersion(DomainSignalId.userRoles).pipe(
      switchMap(() => from(this.identityApi.getRoles(appId, includeDisabled))),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.catalogByApp.set(key, catalog$);

    return catalog$;
  }

  /**
   * Which roles grant an activity on an entity, resolved server-side against the GIN
   * index on the permission map rather than by filtering a downloaded catalogue.
   *
   * Not cached: the entity varies per caller, and these back pickers opened on
   * demand rather than anything on a hot path.
   */
  getRoleIdsByEntity(
    entity: Entity,
    appId: EntityApplications,
    activity: EntityPermissionActivityKeys = EntityPermissionActivityKeys.write,
  ): Observable<string[]> {
    return this.domainSignals.getSignalVersion(DomainSignalId.userRoles).pipe(
      switchMap(() => from(this.identityApi.getRoles(appId, false, entity, activity))),
      map(roles => roles.map(role => role.externalId)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Role id -> display name, for the cell pipes. */
  getRoleNamesById(appId?: EntityApplications): Observable<Map<string, string>> {
    return this.getRoles(appId).pipe(
      map(roles => new Map(roles.map(role => [role.externalId, role.role]))),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }
}
